import type { AuthedRequester } from "@/lib/brokerAuth";
import type { BondSubmissionListItem } from "@/lib/brokerBonds";
import {
  BOND_KINDS,
  SUBMISSION_STATUSES_V2,
  submissionStatusLabel,
  type BondKind,
  type BondPhaseKey,
} from "@/lib/bondStateModel";

// BRK-S406 — the Surety account's BOND HISTORY: one list over the insured's
// bond submissions, the wireframe's nine columns (BRK-S501).
//
// BRK-S435 — FILTERED, TOTALLED AND PAGED BY PMS. `GET /bond/submissions` takes
// `year`, `bondType` and `status` beside `accountId`, pages with `limit` /
// `offset`, and answers the filtered set's aggregates (`totals`) and the years
// the insured's submissions fall in (`years`) — the Year filter's options. The
// SPA sums nothing and reads no other page. PMS-S838 (P3), pinned at BRK-S511;
// `?phase=` (PMS-S859) is how the Active bonds box asks for the same read.
//
// BRK-S436 — the Bond type is the row's served `bondKinds`, a performance and
// payment pair folded into one "Performance & payment", and the Bond type
// filter is the fixed kind vocabulary.

/** The Bond type labels, the wireframe's words. A P&P pair is one type. */
const KIND_LABELS: Record<BondKind, string> = {
  bid: "Bid bond",
  performance: "Performance & payment",
  payment: "Performance & payment",
  supply: "Supply",
  maintenance: "Maintenance",
};

/** What a kind this build has not adopted reads as: neutral, never blank. */
export const UNKNOWN_BOND_TYPE = "Bond";

/** The row's Bond type: its served kinds, labelled, the P&P pair folded, each
 *  label once, in the served order. Null when PMS names no current bond. */
export function bondHistoryType(kinds: readonly unknown[]): string | null {
  const labels = [
    ...new Set(
      kinds.map((k) =>
        typeof k === "string" && Object.hasOwn(KIND_LABELS, k)
          ? KIND_LABELS[k as BondKind]
          : UNKNOWN_BOND_TYPE,
      ),
    ),
  ];
  return labels.length === 0 ? null : labels.join(", ");
}

export interface BondHistoryOption {
  value: string;
  label: string;
}

/** The Bond type filter: the fixed kind vocabulary (BRK-S436), the P&P pair
 *  one option (`performance`, which PMS matches against either half). */
export const BOND_HISTORY_TYPE_OPTIONS: readonly BondHistoryOption[] =
  BOND_KINDS.filter((k) => k !== "payment").map((k) => ({
    value: k,
    label: KIND_LABELS[k],
  }));

/** The v2 submission statuses (proposal §10.1), in lifecycle order, labelled. */
export const BOND_HISTORY_STATUS_OPTIONS: readonly BondHistoryOption[] =
  SUBMISSION_STATUSES_V2.map((s) => ({
    value: s,
    label: submissionStatusLabel(s),
  }));

/** The served aggregates over the FILTERED set (not the page). A figure no
 *  row serves is null — a dash, never $0. */
export interface BondHistoryTotals {
  count: number;
  contractPrice: number | null;
  premium: number | null;
}

/** One page of the history, as PMS answers it (PMS-S838). */
export interface BondHistoryPage {
  items: BondSubmissionListItem[];
  total: number;
  limit: number;
  offset: number;
  totals: BondHistoryTotals;
  /** The years the insured's submissions fall in, newest first — the Year
   *  filter's options, whatever the current filters. */
  years: string[];
}

export interface BondHistoryQuery {
  accountId: string;
  /** Of the bid date, else the day started (PMS-S838). */
  year?: string;
  /** A `BondKind`; `performance` matches either half of a P&P (PMS-S838). */
  bondType?: string;
  status?: string;
  /** The rows whose job is in one of these phases (PMS-S859), comma-joined
   *  on the wire. With no `status`, it replaces PMS's default ended-order
   *  exclusion. */
  phase?: readonly BondPhaseKey[];
  limit: number;
  offset: number;
}

/** A served figure, or null. A formatted string from the wire is not one
 *  (D-10): it reads as absent rather than as a number somebody parsed. */
export function bondHistoryFigure(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function readTotals(raw: unknown): BondHistoryTotals | null {
  if (typeof raw !== "object" || raw === null) return null;
  const t = raw as Record<string, unknown>;
  const count = bondHistoryFigure(t.count);
  if (count === null) return null;
  return {
    count,
    contractPrice: bondHistoryFigure(t.contractPrice),
    premium: bondHistoryFigure(t.premium),
  };
}

/**
 * GET /api/broker/bond/submissions?accountId=&year=&bondType=&status=&limit=&offset=
 * — one page of the insured's history, with PMS's aggregates. Throws on a body
 * without them: a totals row the SPA summed itself is exactly what BRK-S435
 * retired, and the list's error state is the honest answer.
 */
export async function listBondHistory(
  request: AuthedRequester,
  query: BondHistoryQuery,
): Promise<BondHistoryPage> {
  const qs = new URLSearchParams({ accountId: query.accountId });
  if (query.year) qs.set("year", query.year);
  if (query.bondType) qs.set("bondType", query.bondType);
  if (query.status) qs.set("status", query.status);
  if (query.phase && query.phase.length > 0) {
    qs.set("phase", query.phase.join(","));
  }
  qs.set("limit", String(query.limit));
  qs.set("offset", String(query.offset));
  const body = await request<Record<string, unknown>>(
    `/api/broker/bond/submissions?${qs.toString()}`,
  );
  const totals = readTotals(body.totals);
  if (!Array.isArray(body.items) || totals === null) {
    throw new Error("Unreadable bond history.");
  }
  return {
    items: body.items as BondSubmissionListItem[],
    total: bondHistoryFigure(body.total) ?? 0,
    limit: bondHistoryFigure(body.limit) ?? query.limit,
    offset: bondHistoryFigure(body.offset) ?? query.offset,
    totals,
    years: Array.isArray(body.years)
      ? body.years.filter((y): y is string => typeof y === "string")
      : [],
  };
}
